import { useRouteContext } from "@tanstack/react-router";
import { EditorContent, type JSONContent } from "@tiptap/react";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Icon } from "@/components/icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import type { ReplyTarget } from "@/contexts/mobile-reply-context";
import { useEditor } from "@/editor/editor";
import { cn } from "@/utils";
import type { IGif } from "@giphy/js-types";
import { Popover as PopoverPrimitive } from "radix-ui";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from "@/components/ui/drawer";

const LazyEmojiPicker = lazy(() => import("@/components/lazy-emoji-picker"));
const LazyGiphyGrid = lazy(() => import("@/components/lazy-giphy-grid"));

function extractPlainText(node: unknown): string {
	if (!node || typeof node !== "object") return "";
	const n = node as Record<string, unknown>;
	let text = "";
	if (typeof n.text === "string") text += n.text;
	if (n.type === "mention" && (n.attrs as Record<string, unknown>)?.id) {
		text += `@${(n.attrs as Record<string, unknown>).id}`;
	}
	if (Array.isArray(n.content)) {
		for (const child of n.content) {
			text += extractPlainText(child);
		}
	}
	return text;
}

export function MobileCommentComposer({
	onSubmit,
	isLoading,
	initialContent,
	replyTarget,
	onCancelReply,
}: {
	onSubmit: (content: JSONContent) => Promise<any>;
	isLoading: boolean;
	initialContent?: JSONContent;
	replyTarget?: ReplyTarget | null;
	onCancelReply?: () => void;
}) {
	const [hasBottomTabs, setHasBottomTabs] = useState<boolean | null>(null);
	const [emojiOpen, setEmojiOpen] = useState(false);
	const [gifOpen, setGifOpen] = useState(false);
	const [focused, setFocused] = useState(false);
	const [gifSearchTerm, setGifSearchTerm] = useState("");
	const { club, session } = useRouteContext({ from: "__root__" });

	const editor = useEditor({
		editable: true,
		placeholder: "Escreva sua postagem aqui...",
		placeholderType: "first-line",
		session,
		club: club.slug,
		content: initialContent,
		mimeTypes: ["*"],
		features: {
			button: { enabled: true },
			mention: { enabled: true },
			files: { enabled: false },
			image: { enabled: true },
		},
	});

	const prevReplyTargetId = useRef<string | null>(null);

	useEffect(() => {
		if (!editor) return;
		const newId = replyTarget?.commentId ?? null;
		if (newId === prevReplyTargetId.current) return;
		prevReplyTargetId.current = newId;

		if (replyTarget) {
			editor.commands.clearContent(true);
			editor.commands.insertContent([
				{
					type: "mention",
					attrs: { id: replyTarget.authorUsername, label: null },
				},
				{ type: "text", text: " " },
			]);
			editor.commands.focus("end");
		} else {
			editor.commands.clearContent(true);
		}
	}, [replyTarget, editor]);

	const onGifSelect = (gif: IGif) => {
		editor?.commands.setMedia({
			src: gif.images.fixed_height.webp,
			width: "400",
			height: "400",
			alt: `GIF: ${gif.title}`,
		});

		setGifOpen(false);
		setGifSearchTerm("");
	};

	useEffect(() => {
		if (!editor) return;

		const onFocus = () => setFocused(true);
		const onBlur = () => setFocused(false);

		editor.on("focus", onFocus);
		editor.on("blur", onBlur);

		return () => {
			editor.off("focus", onFocus);
			editor.off("blur", onBlur);
		};
	}, [editor]);

	useEffect(() => {
		const bottomTabs = document.querySelector("#bottom-tabs");

		setHasBottomTabs(Boolean(bottomTabs));
	}, []);

	if (hasBottomTabs === null) {
		return null;
	}

	return (
		<>
			<div className="min-h-20 md:hidden" />
			<div
				className={cn(
					"md:hidden fixed z-2 bg-sidebar text-sidebar-foreground border-t border-sidebar-border px-3 py-2 flex flex-col w-full",
					hasBottomTabs ? "bottom-20" : "bottom-0",
				)}
			>
				{replyTarget && (
					<div className="flex items-center justify-between px-1 pb-2 gap-2">
						<div className="flex-1 min-w-0">
							<span className="text-sm font-medium text-primary">
								Respondendo a @{replyTarget.authorDisplayName}
							</span>
							<p className="text-xs text-muted-foreground truncate">
								&ldquo;{extractPlainText(replyTarget.content)}&rdquo;
							</p>
						</div>
						<button
							type="button"
							onClick={onCancelReply}
							className="shrink-0 p-1 text-muted-foreground active:text-foreground"
						>
							<Icon name="X" className="size-4" />
						</button>
					</div>
				)}

				<div className="flex items-start justify-center gap-3">
					{!focused && (
						<div>
							<Avatar>
								<AvatarFallback>{session?.user?.avatarFallback}</AvatarFallback>

								{session?.user?.photo?.signedUrl ? (
									<AvatarImage src={session?.user?.photo?.signedUrl} />
								) : null}
							</Avatar>
						</div>
					)}

					<div className="flex-1 min-w-0 overflow-hidden break-words">
						<div className="flex-1 max-h-[40dvh] w-full gap-3 bg-background border text-foreground p-2 flex flex-col rounded overflow-y-auto">
							<EditorContent editor={editor} />

							{(focused || emojiOpen || gifOpen) && (
								<div className="flex flex-row gap-3 text-foreground">
									<Drawer open={emojiOpen} onOpenChange={setEmojiOpen}>
										<DrawerTrigger asChild>
											<button type="button">
												<Icon name="Smile" className="size-4" />
											</button>
										</DrawerTrigger>
										<DrawerContent>
											<DrawerHeader>
												<DrawerTitle>Emojis</DrawerTitle>
											</DrawerHeader>
											<div className="px-4 pb-4 overflow-y-auto">
												<Suspense
													fallback={<div className="w-[352px] h-[435px]" />}
												>
													<LazyEmojiPicker
														onEmojiSelect={(val: { native: string }) => {
															editor?.commands.insertContent(val.native);
															setEmojiOpen(false);
														}}
													/>
												</Suspense>
											</div>
										</DrawerContent>
									</Drawer>

									<Drawer open={gifOpen} onOpenChange={setGifOpen}>
										<DrawerTrigger asChild>
											<button type="button">GIF</button>
										</DrawerTrigger>
										<DrawerContent>
											<DrawerHeader>
												<DrawerTitle>GIFs</DrawerTitle>
											</DrawerHeader>
											<div className="px-4 pb-4">
												<InputGroup>
													<InputGroupAddon>
														<Icon name="Search" />
													</InputGroupAddon>
													<InputGroupInput
														placeholder="Procurar GIFs..."
														value={gifSearchTerm}
														onChange={(e) => setGifSearchTerm(e.target.value)}
														className="w-full"
													/>
												</InputGroup>

												<div className="h-80 mt-3 overflow-y-auto">
													<Suspense
														fallback={
															<div className="flex h-full items-center justify-center text-muted-foreground">
																Carregando...
															</div>
														}
													>
														<LazyGiphyGrid
															searchTerm={gifSearchTerm}
															width={window.innerWidth - 32}
															onGifClick={onGifSelect}
														/>
													</Suspense>
												</div>

												<Separator className="my-3" />

												<div className="flex items-center justify-center mb-3 text-xs text-muted-foreground">
													Powered by GIPHY
												</div>
											</div>
										</DrawerContent>
									</Drawer>
								</div>
							)}
						</div>
					</div>

					<Button
						variant="default"
						size="icon"
						className="self-end"
						disabled={isLoading}
						onClick={() => {
							if (!editor.isEmpty) {
								onSubmit(editor?.getJSON()).then(() => {
									editor?.commands.clearContent(true);
								});
							}
						}}
					>
						<Icon name="Send" />
					</Button>
				</div>
			</div>
		</>
	);
}

export function CommentComposer({
	onSubmit,
	hideAvatar,
	isLoading,
	initialContent,
}: {
	onSubmit: (content: JSONContent) => Promise<any>;
	hideAvatar?: boolean;
	isLoading: boolean;
	initialContent?: JSONContent;
}) {
	const { club, session } = useRouteContext({ from: "__root__" });

	const [emojiPopoverOpen, setEmojiPopoverOpen] = useState(false);
	const [gifPopoverOpen, setGifPopoverOpen] = useState(false);
	const [gifSearchTerm, setGifSearchTerm] = useState("");

	const editor = useEditor({
		editable: true,
		placeholder: "Escreva sua postagem aqui...",
		placeholderType: "first-line",
		session,
		content: initialContent,
		club: club.slug,
		mimeTypes: ["*"],
		features: {
			button: { enabled: true },
			mention: { enabled: true },
			files: { enabled: false },
			image: { enabled: true },
		},
	});

	const onGifSelect = (gif: IGif) => {
		editor?.commands.setMedia({
			src: gif.images.fixed_height.webp,
			width: "400",
			height: "400",
			alt: `GIF: ${gif.title}`,
		});

		setGifPopoverOpen(false);
		setGifSearchTerm("");
	};

	return (
		<div className="w-full flex-1 flex flex-row items-start justify-center gap-3">
			{!hideAvatar ? (
				<Avatar>
					<AvatarFallback>{session?.user?.avatarFallback}</AvatarFallback>

					{session?.user?.photo?.signedUrl ? (
						<AvatarImage src={session?.user?.photo?.signedUrl} />
					) : null}
				</Avatar>
			) : (
				<div className="size-8" />
			)}

			<div className="flex-1 w-full gap-3 border bg-card text-card-foreground p-2 flex flex-col rounded">
				<EditorContent editor={editor} />

				<div className="flex flex-row gap-1 text-foreground">
					<Popover open={emojiPopoverOpen} onOpenChange={setEmojiPopoverOpen}>
						<PopoverTrigger asChild>
							<Button type="button" variant="ghost" size="sm">
								<Icon name="Smile" className="size-4" />
							</Button>
						</PopoverTrigger>

						<PopoverPrimitive.Portal>
							<PopoverPrimitive.Content data-slot="popover-content">
								<Suspense fallback={<div className="w-[352px] h-[435px]" />}>
									<LazyEmojiPicker
										onEmojiSelect={(val: { native: string }) => {
											editor?.commands.insertContent(val.native);
											setEmojiPopoverOpen(false);
										}}
									/>
								</Suspense>
							</PopoverPrimitive.Content>
						</PopoverPrimitive.Portal>
					</Popover>

					<Popover open={gifPopoverOpen} onOpenChange={setGifPopoverOpen}>
						<PopoverTrigger asChild>
							<Button type="button" variant="ghost" size="sm">
								GIF
							</Button>
						</PopoverTrigger>

						<PopoverContent className="pb-0 w-fit">
							<div>
								<InputGroup>
									<InputGroupAddon>
										<Icon name="Search" />
									</InputGroupAddon>

									<InputGroupInput
										placeholder="Procurar GIFs..."
										value={gifSearchTerm}
										onChange={(e) => setGifSearchTerm(e.target.value)}
										className="w-full"
									/>
								</InputGroup>
							</div>

							<div className="h-80 w-[320px] mt-3 overflow-y-auto">
								<Suspense
									fallback={
										<div className="flex h-full items-center justify-center text-muted-foreground">
											Carregando...
										</div>
									}
								>
									<LazyGiphyGrid
										searchTerm={gifSearchTerm}
										width={20 * 16}
										onGifClick={onGifSelect}
									/>
								</Suspense>
							</div>

							<Separator className="my-3" />

							<div className="flex items-center justify-center mb-3 text-xs text-muted-foreground">
								Powered by GIPHY
							</div>
						</PopoverContent>
					</Popover>
				</div>
			</div>

			<Button
				variant="default"
				size="icon"
				disabled={isLoading}
				onClick={() => {
					if (!editor.isEmpty) {
						onSubmit(editor?.getJSON()).then(() => {
							editor?.commands.clearContent(true);
						});
					}
				}}
			>
				<Icon name="Send" />
			</Button>
		</div>
	);
}
